/** A canvas Number shared by Explore, ship panels, dashboard tiles and exports. */
import uPlot from 'uplot';
import type { NumberResponse } from '../../../api/types.ts';
import { plotState, type PlotState } from '../../../state/plot-state.ts';
import { colorLegend } from '../plot-ui-state.ts';
import { chartContainerSize, getCssVar, themeColors, type ThemeColors } from './chart-dom.ts';
import { rebuildUplot } from './uplot-helpers.ts';

export function formatNumber(value: number | null, decimals: number): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const dp = Math.max(0, Math.min(10, Math.round(decimals)));
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: dp, maximumFractionDigits: dp,
  }).format(Object.is(value, -0) ? 0 : value);
}

export function buildNumberOpts(
  data: NumberResponse,
  c: ThemeColors,
  size: { width: number; height: number },
  state: PlotState = plotState.value,
): { opts: uPlot.Options; uData: uPlot.AlignedData } {
  const label = state.number_label.trim();
  const value = formatNumber(data.value, state.number_decimals);
  const text = data.value == null ? value : `${value}${state.number_unit ? ` ${state.number_unit}` : ''}`;
  const mono = getCssVar('--font-mono');
  const sans = getCssVar('--font-sans');
  const draw = (u: uPlot): void => {
    const ctx = u.ctx;
    const { left, top, width, height } = u.bbox;
    const d = uPlot.pxRatio;
    const small = Math.min(state.style.font_size * d, height * 0.065);
    const padding = small;
    const available = Math.max(1, width - 2 * padding);
    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top, width, height);
    ctx.clip();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let large = Math.min(state.style.font_size * 8 * d, height * 0.3);
    ctx.font = `600 ${large}px ${mono}`;
    const measured = ctx.measureText(text).width;
    if (measured > available) large *= available / measured;
    ctx.font = `600 ${large}px ${mono}`;
    ctx.fillStyle = c.textStrong;
    const center = top + height / 2;
    ctx.fillText(text, left + width / 2, center);
    ctx.font = `500 ${small}px ${sans}`;
    ctx.fillStyle = c.text;
    // Wrap the complete label so saved calculations keep their meaning at tile sizes.
    const words = label.split(/\s+/);
    const lines: string[] = [];
    let line = '';
    for (const word of words) {
      const next = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(next).width > available) {
        lines.push(line); line = word;
      } else line = next;
    }
    if (line) lines.push(line);
    lines.slice(0, 3).forEach((part, i) => ctx.fillText(part, left + width / 2,
      center + large * 0.7 + small * (1.5 + i * 1.4), available));
    if (data.value == null) {
      ctx.fillText(data.source_rows ? 'No result' : 'No matching data', left + width / 2,
        center - large * 0.7 - small, available);
    }
    ctx.restore();
  };
  const accessible = `${label ? `${label}: ` : ''}${data.value == null ? 'No result' : text}`;
  return {
    uData: [[0, 1], [null, null]],
    opts: {
      ...size,
      padding: [0, 0, 0, 0],
      scales: { x: { time: false, range: () => [0, 1] }, y: { range: () => [0, 1] } },
      axes: [{ show: false }, { show: false }],
      series: [{}, { paths: () => null, points: { show: false } }],
      legend: { show: false }, cursor: { show: false },
      hooks: { draw: [draw], ready: [(u) => {
        u.root.setAttribute('role', 'img');
        u.root.setAttribute('aria-label', accessible);
        u.root.title = data.warning || label;
      }] },
    },
  };
}

export function renderNumber(data: NumberResponse): void {
  const { opts, uData } = buildNumberOpts(data, themeColors(), chartContainerSize());
  rebuildUplot(uData, opts);
  colorLegend.set(null);
}
